// src/pages/admin/AdminSchedule.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";

function lastDayOfMonth(y, m /* 1-12 */) {
  return new Date(y, m, 0).getDate();
}
function ymd(y, m, d) {
  const mm = String(m).padStart(2, "0");
  const dd = String(d).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}

export default function AdminSchedule() {
  // --- auth state（簡化：只在儲存時需要登入；讀取人人可 read）---
  const [session, setSession] = useState(null);
  useEffect(() => {
    supabase.auth
      .getSession()
      .then(({ data }) => setSession(data.session || null));
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, s) =>
      setSession(s),
    );
    return () => sub.subscription.unsubscribe();
  }, []);

  // --- 年月選擇 ---
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  // 本月天數與日期清單
  const days = useMemo(() => lastDayOfMonth(year, month), [year, month]);
  const dates = useMemo(
    () => Array.from({ length: days }, (_, i) => i + 1),
    [days],
  );

  // 表格資料：{ [dateKey]: { morning: [a,b], afternoon:[a,b], evening:[a,b] } }
  const [grid, setGrid] = useState({});
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState(null); // success | error | info

  // 從 AdminTeam 使用的 doctors 資料表載入目前啟用中的醫師
  const loadDoctors = async () => {
    setLoadingDoctors(true);

    const { data, error } = await supabase
      .from("doctors")
      .select("id, name, sort_order, created_at")
      .eq("is_active", true)
      .order("sort_order", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: true });

    if (error) {
      console.error("[AdminSchedule] doctors fetch error:", error);
      setDoctors([]);
      setMsg("醫師名單讀取失敗");
      setMsgType("error");
    } else {
      const names = (data || [])
        .map((doctor) => doctor.name?.trim())
        .filter(Boolean);

      setDoctors(names);
    }

    setLoadingDoctors(false);
  };

  // 載入某月份既有 roster
  const loadRoster = async () => {
    setLoading(true);
    setMsg("");
    setMsgType(null);
    const start = ymd(year, month, 1);
    const end = ymd(year, month, lastDayOfMonth(year, month));
    const { data, error } = await supabase
      .from("roster")
      .select("*")
      .gte("date", start)
      .lte("date", end)
      .order("date");

    const next = {};
    if (!error && Array.isArray(data)) {
      for (const r of data) {
        next[r.date] = {
          morning: (r.morning || []).slice(0, 2),
          afternoon: (r.afternoon || []).slice(0, 2),
          evening: (r.evening || []).slice(0, 2),
        };
      }
    } else if (error) {
      console.error(error);
      setMsg("讀取失敗");
      setMsgType("error");
    }
    setGrid(next);
    setLoading(false);
  };
  // 醫師名單只需要初始化時讀取
  useEffect(() => {
    loadDoctors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 切換年月時重新讀取班表
  useEffect(() => {
    loadRoster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  // 改值
  const updateCell = (dateKey, session, idx, value) => {
    setGrid((g) => {
      const row = g[dateKey] || { morning: [], afternoon: [], evening: [] };
      const arr = (row[session] || []).slice(0, 2);
      arr[idx] = value;
      const cleaned = arr.filter(Boolean).slice(0, 2); // 最多兩位
      return { ...g, [dateKey]: { ...row, [session]: cleaned } };
    });
  };

  // 儲存（Upsert 全月）
  const saveMonth = async () => {
    setLoading(true);
    setMsg("");
    setMsgType(null);

    // 檢查登入
    const { data: sess } = await supabase.auth.getSession();
    if (!sess?.session) {
      setLoading(false);
      setMsg(
        "請先登入後台（/admin/notice）或在此頁先登入 Supabase）；再儲存。",
      );
      setMsgType("error");
      return;
    }

    const rows = dates.map((d) => {
      const dateKey = ymd(year, month, d);
      const row = grid[dateKey] || {};
      return {
        date: dateKey,
        morning: (row.morning || []).slice(0, 2),
        afternoon: (row.afternoon || []).slice(0, 2),
        evening: (row.evening || []).slice(0, 2),
      };
    });

    const { error } = await supabase
      .from("roster")
      .upsert(rows, { onConflict: "date" });
    if (error) {
      console.error(error);
      setMsg("儲存失敗");
      setMsgType("error");
    } else {
      setMsg("✅ 已儲存本月班表");
      setMsgType("success");
      loadRoster();
    }
    setLoading(false);
  };

  // ===== CSV 匯出：月曆式（每週 × 早/午/晚） =====
  const WEEK_NAMES = [
    "星期一",
    "星期二",
    "星期三",
    "星期四",
    "星期五",
    "星期六",
    "星期日",
  ];

  // 月份切成以「星期一起算」的週陣列（每週 7 格，跨月為 null）
  function splitByWeeks(y, m) {
    const daysInMonth = lastDayOfMonth(y, m);
    const first = new Date(y, m - 1, 1);
    const offset = (first.getDay() + 6) % 7; // 以星期一為 0
    const weeks = [];
    let w = new Array(7).fill(null);
    let i = offset;
    for (let d = 1; d <= daysInMonth; d++) {
      w[i] = d;
      i++;
      if (i === 7) {
        weeks.push(w);
        w = new Array(7).fill(null);
        i = 0;
      }
    }
    if (i !== 0) weeks.push(w);
    return weeks;
  }

  function csvEscape(v) {
    if (v == null) return "";
    const s = String(v);
    if (
      s.includes('"') ||
      s.includes(",") ||
      s.includes("\n") ||
      s.includes("\r")
    ) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  }

  function formatCell(ymdKey, sessionKey) {
    const row = grid[ymdKey] || {};
    const arr = (row[sessionKey] || []).slice(0, 2);
    const lines = [];
    if (arr[0] && arr[0] !== "—") lines.push(`${arr[0]}(1診)`);
    if (arr[1] && arr[1] !== "—") lines.push(`${arr[1]}(2診)`);
    return lines.join("\n"); // CSV 內同一格換行
  }

  function buildMonthlyCsv(y, m) {
    const weeks = splitByWeeks(y, m);
    const rows = [];

    // 置頂：月份標題列（只放在第一欄，其他留空）
    rows.push([`${m}月`, "", "", "", "", "", "", ""]);

    // 每週：先放「星期X(日期)」標題列，再放早/午/晚 3 列
    weeks.forEach((week) => {
      // 週標題
      const header = [""];
      week.forEach((d, idx) => {
        header.push(d ? `${WEEK_NAMES[idx]}(${d})` : "");
      });
      rows.push(header);

      // 3 列：早上/下午/晚上
      const sessions = [
        { key: "morning", label: "早上" },
        { key: "afternoon", label: "下午" },
        { key: "evening", label: "晚上" },
      ];
      sessions.forEach((s) => {
        const r = [s.label];
        week.forEach((d) => {
          if (!d) {
            r.push("");
            return;
          }
          const key = ymd(y, m, d);
          r.push(formatCell(key, s.key));
        });
        rows.push(r);
      });

      // 週與週之間加空白列（美觀）
      rows.push([""]);
    });

    // 組成 CSV 文字（加 BOM 讓 Excel 正確顯示中文）
    const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\r\n");
    return "\uFEFF" + csv;
  }

  function downloadCsv() {
    const csv = buildMonthlyCsv(year, month);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${year}年${month}月班表.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // UI 樣式（簡化 inline）
  const styles = {
    wrap: { maxWidth: 1080, margin: "24px auto", padding: "0 16px" },
    topBar: { display: "flex", gap: 8, alignItems: "center", marginBottom: 12 },
    select: {
      height: 36,
      borderRadius: 8,
      border: "1px solid #d1d5db",
      padding: "0 10px",
    },
    btn: {
      height: 36,
      padding: "0 12px",
      borderRadius: 8,
      border: "1px solid #0ea5e9",
      background: "#0ea5e9",
      color: "#fff",
      cursor: "pointer",
    },
    card: {
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      background: "#fff",
      overflow: "hidden",
    },
    head: {
      padding: "10px 12px",
      borderBottom: "1px solid #e5e7eb",
      background: "#f8fafc",
      fontWeight: 700,
    },
    grid: { width: "100%", borderCollapse: "collapse" },
    th: {
      background: "#fff7ed",
      color: "#7a4b00",
      padding: 8,
      borderBottom: "1px solid #f1e4d6",
    },
    td: {
      borderBottom: "1px solid #f7efe6",
      borderLeft: "1px solid #f7efe6",
      padding: 8,
      verticalAlign: "top",
    },
    dayNo: { fontWeight: 800, color: "#374151", marginBottom: 6 },
    selects: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 },
    label: { fontSize: 12, color: "#6b7280" },
    msg: (t) => ({
      marginTop: 8,
      padding: "8px 10px",
      borderRadius: 8,
      border: "1px solid",
      ...(t === "success"
        ? { background: "#ecfdf5", borderColor: "#6ee7b7", color: "#065f46" }
        : t === "error"
          ? { background: "#fef2f2", borderColor: "#fecaca", color: "#991b1b" }
          : {
              background: "#f1f5f9",
              borderColor: "#e2e8f0",
              color: "#334155",
            }),
    }),
  };

  return (
    <section style={styles.wrap}>
      <h2 style={{ fontSize: 22, fontWeight: 800, margin: "0 0 10px" }}>
        班表管理
      </h2>

      {/* 年月選擇 */}
      <div style={styles.topBar}>
        <label>年份</label>
        <select
          style={styles.select}
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
        >
          {Array.from({ length: 6 }, (_, i) => now.getFullYear() - 2 + i).map(
            (y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ),
          )}
        </select>

        <label>月份</label>
        <select
          style={styles.select}
          value={month}
          onChange={(e) => setMonth(Number(e.target.value))}
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>

        <button
          style={styles.btn}
          onClick={async () => {
            await Promise.all([loadRoster(), loadDoctors()]);
          }}
          disabled={loading || loadingDoctors}
        >
          {loading || loadingDoctors ? "讀取中…" : "重新讀取"}
        </button>
        <button style={styles.btn} onClick={saveMonth} disabled={loading}>
          {loading ? "處理中…" : "儲存本月班表"}
        </button>
        <button style={styles.btn} onClick={downloadCsv} disabled={loading}>
          下載班表 CSV
        </button>
      </div>

      {/* 表格 */}
      <div style={styles.card}>
        <div style={styles.head}>
          {year} 年 {month} 月
        </div>
        <table style={styles.grid}>
          <thead>
            <tr>
              <th style={styles.th}>日期</th>
              <th style={styles.th}>早診 1</th>
              <th style={styles.th}>早診 2</th>
              <th style={styles.th}>午診 1</th>
              <th style={styles.th}>午診 2</th>
              <th style={styles.th}>晚診 1</th>
              <th style={styles.th}>晚診 2</th>
            </tr>
          </thead>
          <tbody>
            {dates.map((d) => {
              const dateKey = ymd(year, month, d);
              const row = grid[dateKey] || {
                morning: [],
                afternoon: [],
                evening: [],
              };
              const m = row.morning || [];
              const a = row.afternoon || [];
              const e = row.evening || [];
              const opts = doctors;

              return (
                <tr key={dateKey}>
                  <td style={styles.td}>
                    <div style={styles.dayNo}>{d}</div>
                  </td>

                  {/* 早 1 / 2 */}
                  <td style={styles.td}>
                    <div style={styles.label}>早診 1</div>
                    <select
                      value={m[0] || ""}
                      onChange={(ev) =>
                        updateCell(dateKey, "morning", 0, ev.target.value)
                      }
                    >
                      <option value=""></option>
                      {opts.map((n) => (
                        <option key={`m1-${dateKey}-${n}`} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.label}>早診 2</div>
                    <select
                      value={m[1] || ""}
                      onChange={(ev) =>
                        updateCell(dateKey, "morning", 1, ev.target.value)
                      }
                    >
                      <option value=""></option>
                      {opts.map((n) => (
                        <option key={`m2-${dateKey}-${n}`} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* 午 1 / 2 */}
                  <td style={styles.td}>
                    <div style={styles.label}>午診 1</div>
                    <select
                      value={a[0] || ""}
                      onChange={(ev) =>
                        updateCell(dateKey, "afternoon", 0, ev.target.value)
                      }
                    >
                      <option value=""></option>
                      {opts.map((n) => (
                        <option key={`a1-${dateKey}-${n}`} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.label}>午診 2</div>
                    <select
                      value={a[1] || ""}
                      onChange={(ev) =>
                        updateCell(dateKey, "afternoon", 1, ev.target.value)
                      }
                    >
                      <option value=""></option>
                      {opts.map((n) => (
                        <option key={`a2-${dateKey}-${n}`} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* 晚 1 / 2 */}
                  <td style={styles.td}>
                    <div style={styles.label}>晚診 1</div>
                    <select
                      value={e[0] || ""}
                      onChange={(ev) =>
                        updateCell(dateKey, "evening", 0, ev.target.value)
                      }
                    >
                      <option value=""></option>
                      {opts.map((n) => (
                        <option key={`e1-${dateKey}-${n}`} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.label}>晚診 2</div>
                    <select
                      value={e[1] || ""}
                      onChange={(ev) =>
                        updateCell(dateKey, "evening", 1, ev.target.value)
                      }
                    >
                      <option value=""></option>
                      {opts.map((n) => (
                        <option key={`e2-${dateKey}-${n}`} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {msg && <div style={styles.msg(msgType)}>{msg}</div>}
      </div>
    </section>
  );
}
